import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tooltip,
  IconButton,
  CardMedia,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Article as NewspaperIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ResponsiveTitle from '../../components/ResponsiveTitle';
import { useNews } from '../../hooks/useNews';
import { useDeals } from '../../hooks/useDeals';
import { useEventPhotos } from '../events/useEventPhotos';
import { MEMBER_TYPES } from '../../types/User';
import { Event } from '../../types/Event';
import DealCard from '../../components/DealCard';
import { Deal } from '../../services/deals';
import { adminStyles } from '../../theme/theme';

// Fonctions utilitaires
const formatNewsDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const formatEventDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const getRatingColor = (percent?: number): string => {
  if (!percent) return '#888888';
  if (percent >= 85) return '#66BB6A'; // Vert pour très positif
  if (percent >= 70) return '#8BC34A'; // Vert clair pour positif
  if (percent >= 50) return '#FFC107'; // Jaune pour plutôt positif
  if (percent >= 40) return '#FF9800'; // Orange pour mitigé
  return '#F44336'; // Rouge pour négatif
};

interface NewsSectionProps {
  user: any;
  pastEvents?: Event[];
  onPastEventClick?: (eventId: string) => void;
}

type CombinedItem =
  | { type: 'news'; data: any; date: Date }
  | { type: 'event'; data: Event; date: Date };

const NewsSection: React.FC<NewsSectionProps> = ({ user, pastEvents = [], onPastEventClick }) => {
  const navigate = useNavigate();
  const { news, newsLoading } = useNews();
  const { freeGames, loadingFreeGames, freeGamesError, lastUpdated } = useDeals();
  const eventPhotos = useEventPhotos(pastEvents);

  const updateDate = lastUpdated || new Date();

  const handleDealClick = (deal: Deal) => {
    if (deal.otherDeals && deal.otherDeals.length > 0) {
      navigate('/deals');
    } else {
      window.open(deal.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Combiner et trier les actualités et les événements passés
  const combinedItems: CombinedItem[] = useMemo(() => {
    const newsItems: CombinedItem[] = news.map(item => ({
      type: 'news',
      data: item,
      date: new Date(item.publishDate)
    }));

    const eventItems: CombinedItem[] = pastEvents.map(event => ({
      type: 'event',
      data: event,
      date: new Date(event.endDate) // On utilise la date de fin pour l'ordre chronologique inverse
    }));

    return [...newsItems, ...eventItems].sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [news, pastEvents]);

  // Limiter l'affichage à 5 éléments pour la page d'accueil
  const displayedItems = combinedItems.slice(0, 5);

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 0 },
        mb: 4
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: 4,
          px: 3,
          py: 1,
          color: 'white',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <NewspaperIcon />
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <ResponsiveTitle variant="h4">
              Actualités
            </ResponsiveTitle>
          </Box>
        </Box>

        {user?.role === MEMBER_TYPES.ADMIN && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/news/create')}
          >
            Nouvelle actualité
          </Button>
        )}
      </Box>

      {newsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : displayedItems.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Actualité spéciale pour les jeux gratuits et en promotion (toujours en premier) */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Card sx={{ backgroundColor: 'rgba(30, 30, 30, 0.7)', '&:hover': { boxShadow: 'none' } }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0, py: 0 }}>
                  <Typography
                    variant="h5"
                    component="div"
                    sx={{
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', sm: '1.3rem' },
                      mr: 1.5,
                      lineHeight: 0.8,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    Jeux gratuits et bonnes affaires du moment
                  </Typography>

                  <Typography
                    variant="body2"
                    color="rgba(150, 150, 150, 0.7)"
                    sx={{
                      whiteSpace: 'nowrap',
                      display: { xs: 'none', sm: 'inline' },
                      fontSize: '0.8rem',
                      fontStyle: 'italic'
                    }}
                  >
                    Mis à jour le {updateDate.toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>

                  <Box sx={{ flexGrow: 1 }} />
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    display: { xs: 'block', sm: 'none' }
                  }}
                >
                  Mis à jour le {updateDate.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>

                <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                <Typography
                  variant="body1"
                  component="div"
                  sx={{ color: '#ddd', my: 2 }}
                >
                  Découvrez les jeux gratuits et les meilleures promotions du moment sur Steam et Epic Games Store.
                </Typography>

                {/* Affichage limité des jeux (3 maximum) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {loadingFreeGames ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : freeGamesError ? (
                    <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
                      Erreur : {freeGamesError}
                    </Typography>
                  ) : freeGames.length === 0 ? (
                    <Typography sx={{ textAlign: 'center', p: 2, color: '#aaa' }}>
                      Aucune offre disponible pour le moment.
                    </Typography>
                  ) : (
                    <>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                        {freeGames.slice(0, 3).map((game, idx) => (
                          <DealCard
                            key={idx}
                            deal={game}
                            onClick={handleDealClick}
                            sx={{ height: '100%' }}
                          />
                        ))}
                      </Box>

                      {freeGames.length > 3 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate('/deals')}
                            sx={{ mt: 1 }}
                          >
                            Voir tous les jeux ({freeGames.length})
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {displayedItems.map((item: CombinedItem, index: number) => {
            if (item.type === 'news') {
              const newsItem = item.data;
              return (
                <Card key={`news-${newsItem.id}`} sx={{ backgroundColor: 'rgba(30, 30, 30, 0.7)', '&:hover': { boxShadow: 'none' } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0, py: 0 }}>
                      <ResponsiveTitle
                        variant="h5"
                        component="div"
                        sx={{
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '1.1rem', sm: '1.3rem' },
                          mr: 1.5,
                          lineHeight: 0.8,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        dangerouslySetInnerHTML={{ __html: newsItem.title }}
                      />

                      <Typography
                        variant="body2"
                        color="rgba(150, 150, 150, 0.7)"
                        sx={{
                          whiteSpace: 'nowrap',
                          display: { xs: 'none', sm: 'inline' },
                          fontSize: '0.8rem',
                          fontStyle: 'italic'
                        }}
                      >
                        Publié le {formatNewsDate(newsItem.publishDate)} par {newsItem.author}
                      </Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      {user?.role === MEMBER_TYPES.ADMIN && (
                        <Tooltip title="Modifier l'actualité">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/news/edit/${newsItem.id}`)}
                            sx={{
                              ml: 1,
                              ...adminStyles.glassButton,
                              '&:hover': {
                                ...adminStyles.glassButton['&:hover'],
                                bgcolor: 'primary.main'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: { xs: 'block', sm: 'none' }
                      }}
                    >
                      Publié le {formatNewsDate(newsItem.publishDate)} par {newsItem.author}
                    </Typography>

                    <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                    <Typography
                      variant="body1"
                      component="div"
                      sx={{ color: '#ddd', my: 2 }}
                      dangerouslySetInnerHTML={{ __html: newsItem.content }}
                    />

                    {/* Affichage de l'image si elle existe */}
                    {newsItem.imageUrl && (
                      <Box
                        sx={{
                          mb: 3,
                          mt: 4,
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mx: { xs: 1, sm: 2, md: 3 }
                        }}
                      >
                        <Box
                          component="img"
                          src={newsItem.imageUrl}
                          alt={newsItem.title}
                          loading="lazy"
                          sx={{
                            width: 'auto',
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '800px',
                            borderRadius: 2,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            } else {
              const eventItem = item.data;
              const eventImages = eventPhotos[eventItem.id || ''] || [];
              const hasPhotos = eventImages.length > 0;

              // Si on a des photos, on utilise la première comme principale, sinon rien
              const bgImage = hasPhotos ? eventImages[0] : '';

              return (
                <Card key={`event-${eventItem.id}`} sx={{ backgroundColor: 'rgba(30, 30, 30, 0.7)', '&:hover': { boxShadow: 'none' } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0, py: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#fff', mr: 2 }}>
                        <EventIcon sx={{ mr: 1 }} />
                        <Typography variant="overline" sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1 }}>
                          Événement passé
                        </Typography>
                      </Box>

                      <ResponsiveTitle
                        variant="h5"
                        component="div"
                        sx={{
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '1.1rem', sm: '1.3rem' },
                          mr: 1.5,
                          lineHeight: 0.8,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {eventItem.title}
                      </ResponsiveTitle>

                      <Typography
                        variant="body2"
                        color="rgba(150, 150, 150, 0.7)"
                        sx={{
                          whiteSpace: 'nowrap',
                          display: { xs: 'none', sm: 'inline' },
                          fontSize: '0.8rem',
                          fontStyle: 'italic'
                        }}
                      >
                        {formatEventDate(eventItem.startDate)}
                      </Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      {user?.role === MEMBER_TYPES.ADMIN && (
                        <Tooltip title="Voir le debrief / Gérer les photos">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (onPastEventClick && eventItem.id) {
                                onPastEventClick(eventItem.id);
                              } else {
                                navigate(`/events/${eventItem.id}/debrief`);
                              }
                            }}
                            sx={{
                              ml: 1,
                              ...adminStyles.glassButton,
                              '&:hover': {
                                ...adminStyles.glassButton['&:hover'],
                                bgcolor: 'primary.main'
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: { xs: 'block', sm: 'none' }
                      }}
                    >
                      {formatEventDate(eventItem.startDate)}
                    </Typography>

                    <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                    {eventItem.debrief?.content ? (
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{
                          color: '#ddd',
                          my: 2,
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {eventItem.debrief.content}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{ color: '#ddd', my: 2, fontStyle: 'italic' }}
                      >
                        L'événement est terminé ! Retrouvez les photos et le résumé de cette soirée.
                      </Typography>
                    )}

                    {/* Grille de photos (seulement si disponibles) */}
                    {hasPhotos && (
                      <Box sx={{
                        mt: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        alignItems: 'flex-start'
                      }}>
                        {eventImages.slice(0, 4).map((imgUrl, index) => (
                          <Box
                            key={index}
                            onClick={() => {
                              if (onPastEventClick && eventItem.id) {
                                onPastEventClick(eventItem.id);
                              } else {
                                navigate(`/events/${eventItem.id}/debrief`);
                              }
                            }}
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              maxWidth: '100%',
                              flexShrink: 1
                            }}
                          >
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`Photo événement ${index + 1}`}
                              sx={{
                                display: 'block',
                                maxWidth: '100%',
                                width: 'auto',
                                height: { xs: 120, sm: 160, md: 200 },
                                objectFit: 'cover',
                                borderRadius: 1,
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'scale(1.02)' }
                              }}
                            />
                            {/* Si c'est la 4ème image et qu'il y en a plus, afficher un overlay */}
                            {index === 3 && eventImages.length > 4 && (
                              <Box sx={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.2rem',
                                zIndex: 2
                              }}>
                                +{eventImages.length - 4} photos
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            }
          })}

          {/* Bouton pour voir toutes les actualités (garde sa place en bas) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/news')}
              sx={{ mt: 1 }}
            >
              Voir toutes les actualités
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: '#aaa' }}>
          Aucune actualité à afficher pour le moment.
        </Typography>
      )}
    </>
  );
};

export default React.memo(NewsSection);
